<script setup>
import EmailingAutomationPromoCodeTable from '@frontJs/components/tables/projectManagement/modules/EmailingAutomationPromoCodeTable.vue';
import { router } from '@inertiajs/vue3';
import DeleteAction from '@js/components/actions/DeleteAction.vue';
import ActionButton from '@js/components/buttons/ActionButton.vue';
import InputField from '@js/components/fields/InputField.vue';
import ListboxField from '@js/components/fields/ListboxField.vue';
import SwitchField from '@js/components/fields/SwitchField.vue';
import FormColumn from '@js/components/forms/FormColumn.vue';
import FormContent from '@js/components/forms/FormContent.vue';
import FormFooter from '@js/components/forms/FormFooter.vue';
import FormGrid from '@js/components/forms/FormGrid.vue';
import FormRoot from '@js/components/forms/FormRoot.vue';
import FormTitle from '@js/components/forms/FormTitle.vue';
import TabLayout from '@js/components/tabs/TabLayout.vue';
import { useRoute } from '@js/utils/route';
import { toastError, toastSaved } from '@js/utils/toast';
import { computed, reactive, ref } from 'vue';
import { array, boolean, number, object, string } from 'yup';

const { tenant, promoPriceRule, promoDiscountTypes, stores, customerGroups } =
    defineProps({
        tenant: {
            type: Object,
            required: true,
        },
        promoPriceRule: {
            type: Object,
            required: false,
        },
        promoDiscountTypes: {
            type: Array,
            required: true,
        },
        stores: {
            type: Array,
            required: true,
        },
        customerGroups: {
            type: Array,
            required: true,
        },
    });

const { route } = useRoute();

const isSubmitting = ref(false);

const form = reactive({
    name: promoPriceRule ? promoPriceRule.name : '',
    discountType: promoPriceRule
        ? promoPriceRule.discountType.value
        : promoDiscountTypes[0].value,
    discountValue: promoPriceRule ? promoPriceRule.discountValue : null,
    minimumCartAmount: promoPriceRule ? promoPriceRule.minimumCartAmount : null,
    validityDays: promoPriceRule ? promoPriceRule.validityDays : 7,
    websiteIds: promoPriceRule ? promoPriceRule.websiteIds : [],
    customerGroupIds: promoPriceRule ? promoPriceRule.customerGroupIds : [],
    isActive: promoPriceRule ? promoPriceRule.isActive : true,
});

function discountTypeLabel(value) {
    return promoDiscountTypes.find((type) => type.value === value)?.label ?? '';
}

function storeLabel(cmsStoreId) {
    const store = stores.find((item) => item.cmsStoreId === cmsStoreId);

    return store
        ? (store.name ?? store.url ?? `#${cmsStoreId}`)
        : `#${cmsStoreId}`;
}

function customerGroupLabel(cmsCustomerGroupId) {
    const customerGroup = customerGroups.find(
        (item) => item.cmsCustomerGroupId === cmsCustomerGroupId,
    );

    return customerGroup ? customerGroup.code : `#${cmsCustomerGroupId}`;
}

const tabs = computed(() => ({
    configuration: {
        title: 'Configuration',
        contentSlotName: 'configuration',
    },
    ...(promoPriceRule
        ? {
              codes: {
                  title: 'Codes générés',
                  contentSlotName: 'codes',
              },
          }
        : {}),
}));

const validationSchema = object({
    name: string().required('Le nom est requis'),
    discountType: string().required('Le type de réduction est requis'),
    discountValue: number()
        .typeError('La valeur de réduction doit être un nombre')
        .required('La valeur de réduction est requise')
        .min(0, 'La valeur de réduction doit être positive'),
    minimumCartAmount: number()
        .transform((value, originalValue) =>
            originalValue === '' || originalValue === null ? null : value,
        )
        .min(0, 'Le montant minimum de panier doit être positif')
        .nullable(),
    validityDays: number()
        .typeError('La période de validité doit être un nombre')
        .required('La période de validité est requise')
        .min(1, 'La période de validité doit être au moins de 1 jour'),
    websiteIds: array().of(number()).nullable(),
    customerGroupIds: array().of(number()).nullable(),
    isActive: boolean().required('Le statut actif est requis'),
});

function submit() {
    isSubmitting.value = true;

    axios
        .post(
            route(
                'front.projectManagement.modules.postEmailingAutomationPromoPriceRule',
                {
                    tenant: tenant.id,
                    ...(promoPriceRule
                        ? { promoPriceRule: promoPriceRule.id }
                        : {}),
                },
            ),
            {
                name: form.name,
                discountType: form.discountType,
                discountValue: form.discountValue,
                minimumCartAmount: form.minimumCartAmount,
                validityDays: form.validityDays,
                websiteIds: form.websiteIds,
                customerGroupIds: form.customerGroupIds,
                isActive: form.isActive,
            },
        )
        .then(() => {
            toastSaved();

            router.visit(
                route('front.projectManagement.modules.emailingAutomations', {
                    tenant: tenant.id,
                    tab: 'promoCodes',
                }),
            );
        })
        .finally(() => {
            isSubmitting.value = false;
        })
        .catch((error) => {
            toastError(error);
        });
}
</script>

<template>
    <TabLayout :tabs="tabs" :unmount-on-hide="false" :sync-url="false">
        <template #configuration>
            <FormRoot
                v-slot="{ errors }"
                :validation-schema="validationSchema"
                :submit="submit"
            >
                <FormColumn>
                    <FormContent>
                        <FormTitle>Règle de prix promo</FormTitle>

                        <InputField
                            v-model="form.name"
                            label="Nom"
                            legend="Sert de préfixe aux codes générés (ex. « PA10 » → « PA10-7F3K9Q »)."
                            field-name="name"
                            field-id="name"
                            type="text"
                            required
                        />

                        <SwitchField
                            v-model="form.isActive"
                            label="Est actif"
                            field-name="isActive"
                            field-id="isActive"
                        />
                    </FormContent>

                    <FormContent>
                        <FormTitle>Réduction</FormTitle>

                        <FormGrid :columns="3">
                            <ListboxField
                                v-model="form.discountType"
                                field-id="discountType"
                                field-name="discountType"
                                label="Type de réduction"
                                :options="promoDiscountTypes"
                                option-key="value"
                                required
                            >
                                <template #buttonContent>
                                    {{ discountTypeLabel(form.discountType) }}
                                </template>

                                <template #optionContent="{ option }">
                                    {{ option.label }}
                                </template>
                            </ListboxField>

                            <InputField
                                v-model="form.discountValue"
                                label="Valeur de réduction"
                                legend="Pourcentage (ex. 10) ou montant fixe selon le type."
                                field-name="discountValue"
                                field-id="discountValue"
                                type="number"
                                min="0"
                                step="0.01"
                                required
                            />

                            <InputField
                                v-model="form.minimumCartAmount"
                                label="Montant minimum de panier"
                                legend="Laisser vide pour ne pas exiger de minimum."
                                field-name="minimumCartAmount"
                                field-id="minimumCartAmount"
                                type="number"
                                min="0"
                                step="0.01"
                            />
                        </FormGrid>
                    </FormContent>

                    <FormContent>
                        <FormTitle>Ciblage CMS</FormTitle>

                        <FormGrid :columns="2">
                            <ListboxField
                                v-model="form.websiteIds"
                                field-id="websiteIds"
                                field-name="websiteIds"
                                label="Sites"
                                legend="Sites e-commerce sur lesquels le code est valable côté CMS."
                                :options="stores"
                                option-key="cmsStoreId"
                                multiple
                            >
                                <template #buttonContent>
                                    <template
                                        v-if="
                                            form.websiteIds &&
                                            form.websiteIds.length
                                        "
                                    >
                                        {{
                                            form.websiteIds
                                                .map(storeLabel)
                                                .join(', ')
                                        }}
                                    </template>

                                    <template v-else>&nbsp;</template>
                                </template>

                                <template #optionContent="{ option }">
                                    {{
                                        option.name ??
                                        option.url ??
                                        `#${option.cmsStoreId}`
                                    }}
                                </template>
                            </ListboxField>

                            <ListboxField
                                v-model="form.customerGroupIds"
                                field-id="customerGroupIds"
                                field-name="customerGroupIds"
                                label="Groupes clients"
                                legend="Groupes clients autorisés à utiliser le code côté CMS."
                                :options="customerGroups"
                                option-key="cmsCustomerGroupId"
                                multiple
                            >
                                <template #buttonContent>
                                    <template
                                        v-if="
                                            form.customerGroupIds &&
                                            form.customerGroupIds.length
                                        "
                                    >
                                        {{
                                            form.customerGroupIds
                                                .map(customerGroupLabel)
                                                .join(', ')
                                        }}
                                    </template>

                                    <template v-else>&nbsp;</template>
                                </template>

                                <template #optionContent="{ option }">
                                    {{ option.code }}
                                </template>
                            </ListboxField>
                        </FormGrid>
                    </FormContent>

                    <FormContent>
                        <FormTitle>Validité</FormTitle>

                        <FormGrid>
                            <InputField
                                v-model="form.validityDays"
                                label="Période de validité (jours)"
                                legend="Durée de la fenêtre d'envoi de chaque code, et durée de validité restante garantie au client après réception. Un code unique « tourne » automatiquement : il est attribué aux emails pendant sa fenêtre d'envoi (la période de validité), puis reste utilisable par le client la même durée après. Les codes sont générés d'avance et poussés au CMS."
                                field-name="validityDays"
                                field-id="validityDays"
                                type="number"
                                min="1"
                                step="1"
                                required
                            />
                        </FormGrid>
                    </FormContent>

                    <FormContent v-if="promoPriceRule">
                        <FormTitle>Codes générés</FormTitle>

                        <FormGrid>
                            <InputField
                                :model-value="promoPriceRule.codesCount"
                                label="Nombre de codes générés"
                                legend="Codes créés d'avance et poussés au CMS pour couvrir les fenêtres d'envoi à venir."
                                field-name="codesCount"
                                field-id="codesCount"
                                type="number"
                                size="small"
                                disabled
                            />
                        </FormGrid>
                    </FormContent>
                </FormColumn>

                <FormFooter :is-submitting="isSubmitting" :errors="errors">
                    <template #actions>
                        <DeleteAction
                            v-if="promoPriceRule"
                            :url="
                                route(
                                    'front.projectManagement.modules.deleteEmailingAutomationPromoPriceRule',
                                    {
                                        tenant: tenant.id,
                                        promoPriceRule: promoPriceRule.id,
                                    },
                                )
                            "
                            :callback="
                                () => {
                                    router.visit(
                                        route(
                                            'front.projectManagement.modules.emailingAutomations',
                                            {
                                                tenant: tenant.id,
                                                tab: 'promoCodes',
                                            },
                                        ),
                                    );
                                }
                            "
                        >
                            <template #trigger>
                                <ActionButton type="secondary">
                                    Supprimer
                                </ActionButton>
                            </template>
                        </DeleteAction>
                    </template>
                </FormFooter>
            </FormRoot>
        </template>

        <template v-if="promoPriceRule" #codes>
            <EmailingAutomationPromoCodeTable
                :tenant="tenant"
                :promo-price-rule="promoPriceRule"
            />
        </template>
    </TabLayout>
</template>
